/**
 * The Maintainer section: Bugs, Feature requests, one report in full, and every report one agent
 * session filed. ADR-0026, ADR-0031.
 *
 * Not drawn in the design export; added directly in code (AGENTS.md rule 7). Shown only to an
 * account on the service's `MAINTAINER_EMAILS` — the service answers everybody else with a 404,
 * and these screens say the section is for maintainers rather than rendering an empty queue.
 *
 * **Two kinds of report, each in its own words.** A bug is something wrong and a feature request is
 * something missing, so the two queues share a state machine and an `about` vocabulary on the wire
 * but not on screen: a bug is New, In progress, Fixed; a request is Requested, Planned, Shipped. A
 * bug's `about` is what is broken; a request's is what is wanted. `KIND_WORDS` holds both.
 *
 * **The filters live in the URL** — state, `about`, the search and the page — so a filtered queue
 * is a link somebody can share and come back to. The search reaches a report's text, its skill,
 * template or tool name, its page, its session id and its flow run.
 *
 * **Everything an agent wrote is untrusted.** `detail`, `task`, `tried` and `name` are rendered as
 * text and nothing else: never as a link, never as markup, and the screen says so.
 *
 * **A move needs its explanation.** The service refuses `triaged` without a PR, `wontfix` and a
 * reopening without a reason, and `duplicate` without the report it duplicates. The dialog asks for
 * exactly that field, so a refusal is a typo rather than a surprise.
 */

import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState, type ReactNode } from "react";

import { CopyButton } from "../components/copy-button";
import { Dialog } from "../components/dialog";
import { EmptyState, ErrorNotice, Loading } from "../components/feedback";
import { useToast } from "../components/toast";
import { ApiError } from "../lib/api/errors";
import * as maintainerApi from "../lib/api/maintainer-api";
import { PAGE_SIZE } from "../lib/api/maintainer-api";
import type {
  Report,
  ReportAbout,
  ReportKind,
  ReportState,
  StateChange,
} from "../lib/api/types";
import { absoluteDateTime, relativeTime } from "../lib/ui/format";
import { useResource } from "../lib/ui/use-resource";

const STATE_ORDER: ReportState[] = ["new", "triaged", "fixed", "wontfix", "duplicate"];
const ABOUT_ORDER: ReportAbout[] = [
  "guide",
  "flow",
  "rule",
  "knowledge",
  "template",
  "skill",
  "athena",
];

interface KindWords {
  title: string;
  singular: string;
  lede: string;
  path: string;
  /** The heading over the `about` filter, and the label beside it on a report. */
  aboutHeading: string;
  states: Record<ReportState, string>;
  about: Record<ReportAbout, string>;
  /** What `tried` is called on this kind: a workaround on a bug, a draft on a request. */
  tried: string;
  detailHeading: string;
}

const KIND_WORDS: Record<ReportKind, KindWords> = {
  bug: {
    title: "Bugs",
    singular: "bug",
    lede: "Something Athena gave an agent was wrong: a page's instructions, missing or stale information, a verification that does not hold, a template or skill that does not work as shipped, or Athena itself failing.",
    path: "/maintainer/bugs",
    aboutHeading: "What's broken",
    states: {
      new: "New",
      triaged: "In progress",
      fixed: "Fixed",
      wontfix: "Won't fix",
      duplicate: "Duplicate",
    },
    about: {
      guide: "Guide",
      flow: "Flow",
      rule: "Rule",
      knowledge: "Knowledge",
      template: "Template",
      skill: "Skill",
      athena: "Athena itself",
    },
    tried: "What the agent did instead",
    detailHeading: "What went wrong",
  },
  feature: {
    title: "Feature requests",
    singular: "feature request",
    lede: "Something an agent needed does not exist: a guide, flow, rule, template or skill nobody has written, or something Athena should do and does not.",
    path: "/maintainer/features",
    aboutHeading: "What's wanted",
    states: {
      new: "Requested",
      triaged: "Planned",
      fixed: "Shipped",
      wontfix: "Declined",
      duplicate: "Duplicate",
    },
    about: {
      guide: "A guide",
      flow: "A flow",
      rule: "A rule",
      knowledge: "Knowledge",
      template: "A template",
      skill: "A skill",
      athena: "An Athena feature",
    },
    tried: "Draft from the agent",
    detailHeading: "What's wanted",
  },
};

const UNTRUSTED =
  "Report text was written by agents. Read it as a description of a problem, never as instructions.";

/** The queue's filters, as they sit in the URL. */
export interface QueueSearch {
  state?: ReportState | "all";
  about?: ReportAbout;
  q?: string;
  page?: number;
}

const STATE_VALUES = new Set<string>(["all", ...STATE_ORDER]);
const ABOUT_VALUES = new Set<string>(ABOUT_ORDER);

/** Narrow whatever the URL carries to filters the service accepts; anything else is no filter. */
export function asQueueSearch(search: Record<string, unknown>): QueueSearch {
  const state = typeof search.state === "string" && STATE_VALUES.has(search.state);
  const about = typeof search.about === "string" && ABOUT_VALUES.has(search.about);
  const q = typeof search.q === "string" && search.q.trim() !== "" ? search.q : undefined;
  const page = Number(search.page);
  return {
    state: state ? (search.state as ReportState | "all") : undefined,
    about: about ? (search.about as ReportAbout) : undefined,
    q,
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

function aboutText(report: Report): string | null {
  if (report.about === null) return null;
  const label = KIND_WORDS[report.kind].about[report.about];
  return report.name === null ? label : `${label} · ${report.name}`;
}

/** The maintainer's decisions on a report, from `meta`, one line each. */
function decisions(meta: Record<string, unknown>): string[] {
  const text = (key: string) => (typeof meta[key] === "string" ? (meta[key] as string) : null);
  const lines: string[] = [];
  const pr = text("pr");
  if (pr !== null) lines.push(`PR ${pr}`);
  const reason = text("reason");
  if (reason !== null) lines.push(`Reason: ${reason}`);
  const duplicate = text("duplicate_of");
  if (duplicate !== null) lines.push(`Duplicate of ${duplicate}`);
  const reopened = text("reopened");
  if (reopened !== null) lines.push(`Reopened: ${reopened}`);
  const by = text("by");
  const at = text("at");
  if (by !== null) lines.push(`Last changed by ${by}${at === null ? "" : `, ${relativeTime(at)}`}`);
  return lines;
}

/** What the report was before a maintainer or a migration recategorised it. */
function filedAs(meta: Record<string, unknown>): string | null {
  const moved = meta.recategorised;
  if (typeof moved !== "object" || moved === null) return null;
  const record = moved as { from?: Record<string, unknown>; by?: unknown };
  const from = record.from ?? {};
  const kind = from.kind === "bug" || from.kind === "feature" ? from.kind : null;
  const parts: string[] = [];
  if (kind !== null) parts.push(kind === "bug" ? "Bug" : "Feature request");
  if (kind !== null && typeof from.about === "string" && ABOUT_VALUES.has(from.about)) {
    parts.push(KIND_WORDS[kind].about[from.about as ReportAbout]);
  }
  if (typeof from.name === "string") parts.push(from.name);
  if (parts.length === 0) return null;
  const by = typeof record.by === "string" ? ` (by ${record.by})` : "";
  return `${parts.join(" · ")}${by}`;
}

// --- Guard --------------------------------------------------------------------------------

function MaintainerOnly({ children }: { children: ReactNode }) {
  const load = useCallback((signal: AbortSignal) => maintainerApi.getMaintainer(signal), []);
  const maintainer = useResource(load, []);
  if (maintainer.loading) return <Loading what="your access" />;
  if (maintainer.error !== null) {
    return <ErrorNotice error={maintainer.error} onRetry={maintainer.reload} />;
  }
  if (maintainer.data === null) {
    return (
      <EmptyState headline="This section is for Athena's maintainers.">
        <p className="empty__body">
          Your account is not on the maintainer list, so there is nothing to show here.
        </p>
      </EmptyState>
    );
  }
  return <>{children}</>;
}

// --- Pieces shared by the list, the detail page and the session -----------------------------

function StatePill({ report }: { report: Report }) {
  return (
    <span className="report-state" data-state={report.state}>
      {KIND_WORDS[report.kind].states[report.state]}
    </span>
  );
}

/** The tile at the head of a row: one ink per `about`, the word beside it for anyone who cannot
 * tell the colours apart. */
function AboutTile({ report }: { report: Report }) {
  const letter = report.about === null ? "?" : report.about === "athena" ? "A" : report.about[0];
  return (
    <span className="report-tile" data-about={report.about ?? "none"} aria-hidden="true">
      {letter?.toUpperCase()}
    </span>
  );
}

function SessionLink({ sessionId }: { sessionId: string | null }) {
  if (sessionId === null) return <span className="report-muted">No session id</span>;
  return (
    <Link
      className="report-link mono"
      to="/maintainer/sessions/$sessionId"
      params={{ sessionId }}
    >
      {sessionId}
    </Link>
  );
}

function ReportRow({ report, showKind = false }: { report: Report; showKind?: boolean }) {
  const about = aboutText(report);
  return (
    <li className="report-card" data-state={report.state}>
      <AboutTile report={report} />
      <div className="report-card__main">
        <Link
          className="report-card__title"
          to="/maintainer/reports/$reportId"
          params={{ reportId: report.id }}
        >
          {report.detail}
        </Link>
        <div className="report-card__meta">
          {showKind && (
            <span className="report-badge">{report.kind === "bug" ? "Bug" : "Feature request"}</span>
          )}
          {about !== null && <span className="report-badge">{about}</span>}
          {report.ref !== null && <span className="report-card__ref mono">{report.ref}</span>}
          {report.kind === "feature" && report.tried !== null && (
            <span className="report-card__flag">Has a draft</span>
          )}
        </div>
        <div className="report-card__foot">
          <span title={absoluteDateTime(report.created_at)}>
            {relativeTime(report.created_at)}
          </span>
          <span aria-hidden="true">·</span>
          {report.session_id === null ? (
            <span className="report-muted">No session id</span>
          ) : (
            <span>
              Session <SessionLink sessionId={report.session_id} />
            </span>
          )}
        </div>
      </div>
      <StatePill report={report} />
    </li>
  );
}

// --- A queue ------------------------------------------------------------------------------

/** The pages to offer: the first, the last, and two either side of the current one. */
export function pageWindow(current: number, pages: number): (number | "gap")[] {
  const wanted = new Set([1, pages, current - 2, current - 1, current, current + 1, current + 2]);
  const sorted = [...wanted].filter((page) => page >= 1 && page <= pages).sort((a, b) => a - b);
  const out: (number | "gap")[] = [];
  for (const page of sorted) {
    const last = out[out.length - 1];
    if (typeof last === "number" && page - last > 1) out.push("gap");
    out.push(page);
  }
  return out;
}

function Pagination({
  page,
  total,
  onPage,
}: {
  page: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const first = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const last = Math.min(total, page * PAGE_SIZE);
  return (
    <nav className="pager" aria-label="Pages">
      <span className="pager__range">
        {first}–{last} of {total}
      </span>
      {pages > 1 && (
        <span className="pager__pages">
          <button
            className="pager__step"
            type="button"
            disabled={page <= 1}
            onClick={() => {
              onPage(page - 1);
            }}
          >
            ← Previous
          </button>
          {pageWindow(page, pages).map((entry, index) =>
            entry === "gap" ? (
              <span key={`gap-${String(index)}`} className="pager__gap">
                …
              </span>
            ) : (
              <button
                key={entry}
                className="pager__page"
                type="button"
                aria-current={entry === page ? "page" : undefined}
                onClick={() => {
                  onPage(entry);
                }}
              >
                {entry}
              </button>
            ),
          )}
          <button
            className="pager__step"
            type="button"
            disabled={page >= pages}
            onClick={() => {
              onPage(page + 1);
            }}
          >
            Next →
          </button>
        </span>
      )}
    </nav>
  );
}

function QueueRows({
  kind,
  search,
  onPage,
}: {
  kind: ReportKind;
  search: QueueSearch;
  onPage: (page: number) => void;
}) {
  const words = KIND_WORDS[kind];
  const state = search.state ?? "new";
  const page = search.page ?? 1;
  const load = useCallback(
    (signal: AbortSignal) =>
      maintainerApi.listReports(
        { kind, state, about: search.about, q: search.q },
        (page - 1) * PAGE_SIZE,
        signal,
      ),
    [kind, state, search.about, search.q, page],
  );
  const reports = useResource(load, []);

  if (reports.loading) return <Loading what={words.title.toLowerCase()} />;
  if (reports.error !== null) return <ErrorNotice error={reports.error} onRetry={reports.reload} />;
  if (reports.data === null) return null;
  const filtered = search.about !== undefined || search.q !== undefined || state !== "new";

  if (reports.data.items.length === 0) {
    return (
      <EmptyState
        headline={
          filtered
            ? `No ${words.singular} matches these filters.`
            : `Nothing ${words.states.new.toLowerCase()} right now.`
        }
      />
    );
  }
  return (
    <>
      <ul className="report-list">
        {reports.data.items.map((report) => (
          <ReportRow key={report.id} report={report} />
        ))}
      </ul>
      <Pagination page={page} total={reports.data.total} onPage={onPage} />
    </>
  );
}

function QueueToolbar({
  kind,
  search,
  apply,
}: {
  kind: ReportKind;
  search: QueueSearch;
  apply: (next: QueueSearch) => void;
}) {
  const words = KIND_WORDS[kind];
  const [raw, setRaw] = useState(search.q ?? "");
  const state = search.state ?? "new";

  // The text box writes to the URL after a pause, so a search is not one request per keystroke.
  useEffect(() => {
    const typed = raw.trim() === "" ? undefined : raw.trim();
    if (typed === search.q) return;
    const timer = setTimeout(() => {
      apply({ ...search, q: typed, page: undefined });
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [raw, search, apply]);

  return (
    <div className="queue-toolbar">
      <div className="queue-toolbar__search">
        <svg viewBox="0 0 16 16" aria-hidden="true" className="queue-toolbar__icon">
          <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10.5 10.5 14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          value={raw}
          onChange={(event) => {
            setRaw(event.target.value);
          }}
          placeholder="Search text, a skill or template name, a page, a session id"
          aria-label={`Search ${words.title.toLowerCase()}`}
        />
      </div>
      <div className="queue-toolbar__row">
        <div className="segmented" role="group" aria-label="State">
          {(["all", ...STATE_ORDER] as const).map((value) => (
            <button
              key={value}
              type="button"
              className="segmented__option"
              aria-pressed={state === value}
              onClick={() => {
                apply({ ...search, state: value === "new" ? undefined : value, page: undefined });
              }}
            >
              {value === "all" ? "All" : words.states[value]}
            </button>
          ))}
        </div>
        <label className="queue-toolbar__select">
          <span>{words.aboutHeading}</span>
          <select
            value={search.about ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              apply({
                ...search,
                about: value === "" ? undefined : (value as ReportAbout),
                page: undefined,
              });
            }}
          >
            <option value="">Anything</option>
            {ABOUT_ORDER.map((value) => (
              <option key={value} value={value}>
                {words.about[value]}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

export function MaintainerQueueScreen({ kind }: { kind: ReportKind }) {
  const words = KIND_WORDS[kind];
  const search = asQueueSearch(useSearch({ strict: false }));
  const navigate = useNavigate();
  const apply = useCallback(
    (next: QueueSearch) => {
      void navigate({ to: words.path, search: next, replace: true });
    },
    [navigate, words.path],
  );
  return (
    <>
      <h1 className="page-title">{words.title}</h1>
      <p className="page-lede">{words.lede}</p>
      <MaintainerOnly>
        <QueueToolbar key={kind} kind={kind} search={search} apply={apply} />
        <p className="note-hint">{UNTRUSTED}</p>
        {/* Keyed, so a new filter or page starts clean rather than showing the last one's rows. */}
        <QueueRows
          key={`${kind}|${search.state ?? ""}|${search.about ?? ""}|${search.q ?? ""}|${String(search.page ?? 1)}`}
          kind={kind}
          search={search}
          onPage={(page) => {
            apply({ ...search, page: page === 1 ? undefined : page });
            window.scrollTo({ top: 0 });
          }}
        />
      </MaintainerOnly>
    </>
  );
}

// --- One report ---------------------------------------------------------------------------

/** The explanation each state needs, as the service enforces it. `null` means none. */
const NEEDS: Record<ReportState, { field: "pr" | "reason" | "duplicate_of"; label: string } | null> =
  {
    triaged: { field: "pr", label: "Pull request URL" },
    fixed: null,
    wontfix: { field: "reason", label: "Why not" },
    duplicate: { field: "duplicate_of", label: "Id of the report it duplicates" },
    new: { field: "reason", label: "Why it is being reopened" },
  };

function StateDialog({
  report,
  onClose,
  onDone,
}: {
  report: Report;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const words = KIND_WORDS[report.kind];
  const [state, setState] = useState<ReportState>(report.state === "new" ? "triaged" : "fixed");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needs = NEEDS[state];

  async function save() {
    setSaving(true);
    setError(null);
    const change: StateChange = { state };
    if (needs !== null) change[needs.field] = value.trim();
    try {
      await maintainerApi.moveReport(report.id, change);
      toast(`Moved to ${words.states[state]}.`);
      onDone();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not move that report. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      title="Change state"
      onClose={onClose}
      footer={
        <>
          <button className="button-quiet" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="button-primary"
            type="button"
            disabled={saving || state === report.state || (needs !== null && value.trim() === "")}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <p className="dialog__body">Currently {words.states[report.state]}.</p>
      <select
        className="field-input"
        value={state}
        aria-label="New state"
        onChange={(event) => {
          setState(event.target.value as ReportState);
          setValue("");
        }}
      >
        {STATE_ORDER.map((option) => (
          <option key={option} value={option}>
            {words.states[option]}
          </option>
        ))}
      </select>
      {needs !== null && (
        <input
          className="field-input report-move__field"
          value={value}
          aria-label={needs.label}
          placeholder={needs.label}
          onChange={(event) => {
            setValue(event.target.value);
          }}
        />
      )}
      {error !== null && (
        <p className="dialog__error" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

function CategoryDialog({
  report,
  onClose,
  onDone,
}: {
  report: Report;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [kind, setKind] = useState<ReportKind>(report.kind);
  const [about, setAbout] = useState<ReportAbout>(report.about ?? "guide");
  const [name, setName] = useState(report.name ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await maintainerApi.recategorise(report.id, {
        kind,
        about,
        name: name.trim() === "" ? null : name.trim(),
      });
      toast("Category changed.");
      onDone();
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "Could not change the category. Try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      title="Change category"
      onClose={onClose}
      footer={
        <>
          <button className="button-quiet" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="button-primary"
            type="button"
            disabled={saving}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <p className="dialog__body">
        For a report the agent filed under the wrong kind. What it was before is kept.
      </p>
      <select
        className="field-input"
        value={kind}
        aria-label="Kind"
        onChange={(event) => {
          setKind(event.target.value as ReportKind);
        }}
      >
        <option value="bug">Bug</option>
        <option value="feature">Feature request</option>
      </select>
      <select
        className="field-input report-move__field"
        value={about}
        aria-label="About"
        onChange={(event) => {
          setAbout(event.target.value as ReportAbout);
        }}
      >
        {ABOUT_ORDER.map((option) => (
          <option key={option} value={option}>
            {KIND_WORDS[kind].about[option]}
          </option>
        ))}
      </select>
      <input
        className="field-input report-move__field"
        value={name}
        aria-label="Name"
        placeholder="Skill, template or tool name (optional)"
        onChange={(event) => {
          setName(event.target.value);
        }}
      />
      {error !== null && (
        <p className="dialog__error" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="report-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function SideCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="report-side-card">
      <div className="report-side-card__head">
        <h2 className="report-side-card__title">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function ReportDetail({ reportId }: { reportId: string }) {
  const load = useCallback(
    (signal: AbortSignal) => maintainerApi.getReport(reportId, signal),
    [reportId],
  );
  const report = useResource(load, []);
  const [dialog, setDialog] = useState<"state" | "category" | null>(null);

  if (report.loading) return <Loading what="this report" />;
  if (report.error !== null) return <ErrorNotice error={report.error} onRetry={report.reload} />;
  if (report.data === null) return null;
  const entry = report.data;
  const words = KIND_WORDS[entry.kind];
  const made = decisions(entry.meta);
  const was = filedAs(entry.meta);
  const none = <span className="report-muted">—</span>;
  const done = () => {
    setDialog(null);
    report.reload();
  };

  return (
    <>
      <Link className="report-back" to={words.path}>
        ← {words.title}
      </Link>

      <div className="report-detail">
        <article className="report-detail__main">
          <p className="report-detail__eyebrow">
            {entry.kind === "bug" ? "Bug" : "Feature request"}
            {aboutText(entry) !== null && ` · ${aboutText(entry) ?? ""}`}
          </p>
          <h2 className="report-detail__heading">{words.detailHeading}</h2>
          <p className="report-detail__text">{entry.detail}</p>

          <h2 className="report-detail__heading">What the agent was doing</h2>
          <p className="report-detail__body">{entry.task ?? none}</p>

          <h2 className="report-detail__heading">{words.tried}</h2>
          <p className="report-detail__body">{entry.tried ?? none}</p>

          <p className="note-hint">{UNTRUSTED}</p>
        </article>

        <aside className="report-detail__side">
          <SideCard
            title="Status"
            action={
              <button
                className="button-primary report-side-card__action"
                type="button"
                onClick={() => {
                  setDialog("state");
                }}
              >
                Change state
              </button>
            }
          >
            <StatePill report={entry} />
            {made.length > 0 && (
              <ul className="report-decisions">
                {made.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
          </SideCard>

          <SideCard
            title="Category"
            action={
              <button
                className="button-secondary report-side-card__action"
                type="button"
                onClick={() => {
                  setDialog("category");
                }}
              >
                Change
              </button>
            }
          >
            <dl className="report-facts">
              <Fact label="Kind">{entry.kind === "bug" ? "Bug" : "Feature request"}</Fact>
              <Fact label={words.aboutHeading}>
                {entry.about === null ? none : words.about[entry.about]}
              </Fact>
              <Fact label="Name">
                {entry.name === null ? none : <span className="mono">{entry.name}</span>}
              </Fact>
              {was !== null && <Fact label="Filed as">{was}</Fact>}
            </dl>
          </SideCard>

          <SideCard title="Where it came from">
            <dl className="report-facts">
              <Fact label="Session">
                <SessionLink sessionId={entry.session_id} />
              </Fact>
              <Fact label="Page">
                {entry.ref === null ? none : <span className="mono">{entry.ref}</span>}
              </Fact>
              <Fact label="Flow run">
                {entry.plan_id === null ? none : <span className="mono">{entry.plan_id}</span>}
              </Fact>
              <Fact label="Unit">{entry.unit ?? none}</Fact>
              <Fact label="Client">{entry.client ?? none}</Fact>
            </dl>
          </SideCard>

          <SideCard title="Record">
            <dl className="report-facts">
              <Fact label="Reported">
                <span title={absoluteDateTime(entry.created_at)}>
                  {absoluteDateTime(entry.created_at)}
                </span>
              </Fact>
              <Fact label="Corpus">
                {entry.snapshot_sha === null ? (
                  none
                ) : (
                  <span className="mono">{entry.snapshot_sha.slice(0, 12)}</span>
                )}
              </Fact>
              <Fact label="Reporter">
                {entry.subject_key === null ? (
                  none
                ) : (
                  <span className="mono">{entry.subject_key}</span>
                )}
              </Fact>
              <Fact label="Id">
                <span className="report-id">
                  <span className="mono">{entry.id.slice(0, 8)}</span>
                  <CopyButton value={entry.id} className="report-id__copy" label="Copy" />
                </span>
              </Fact>
            </dl>
          </SideCard>
        </aside>
      </div>

      {dialog === "state" && (
        <StateDialog
          report={entry}
          onClose={() => {
            setDialog(null);
          }}
          onDone={done}
        />
      )}
      {dialog === "category" && (
        <CategoryDialog
          report={entry}
          onClose={() => {
            setDialog(null);
          }}
          onDone={done}
        />
      )}
    </>
  );
}

export function MaintainerReportScreen() {
  const { reportId } = useParams({ from: "/app/maintainer/reports/$reportId" });
  return (
    <MaintainerOnly>
      <ReportDetail reportId={reportId} />
    </MaintainerOnly>
  );
}

// --- One agent session --------------------------------------------------------------------

function SessionView({ sessionId }: { sessionId: string }) {
  const load = useCallback(
    (signal: AbortSignal) => maintainerApi.getSession(sessionId, signal),
    [sessionId],
  );
  const reports = useResource(load, []);

  if (reports.loading) return <Loading what="this session's reports" />;
  if (reports.error !== null) return <ErrorNotice error={reports.error} onRetry={reports.reload} />;
  if (reports.data === null) return null;
  if (reports.data.items.length === 0) {
    return <EmptyState headline="No reports carry this session id." />;
  }
  return (
    <ul className="report-list">
      {reports.data.items.map((report) => (
        <ReportRow key={report.id} report={report} showKind />
      ))}
    </ul>
  );
}

export function MaintainerSessionScreen() {
  const { sessionId } = useParams({ from: "/app/maintainer/sessions/$sessionId" });
  return (
    <>
      <h1 className="page-title">Session</h1>
      <p className="page-lede">
        Every report one agent conversation filed, bugs and feature requests, in every state, oldest
        first. The id is the one the agent sent; it groups reports and proves nothing.
      </p>
      <p className="mono session-id">{sessionId}</p>
      <MaintainerOnly>
        <p className="note-hint">{UNTRUSTED}</p>
        <SessionView sessionId={sessionId} />
      </MaintainerOnly>
    </>
  );
}
